import React, { useEffect, useState } from "react";

/**
 * Reusable confirmation / alert modal.
 *
 * Use for:
 * - Replacing window.confirm (mode="confirm")
 * - Replacing window.alert (mode="alert")
 *
 * When onConfirm returns a Promise:
 * - If it resolves, the modal closes.
 * - If it rejects, the error message is shown inside the modal (and onError is called if provided);
 *   the modal stays open so the user can cancel or fix and retry.
 */
function ConfirmationModal({
  open,
  title,
  message,
  mode = "alert", // "alert" | "confirm"
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  onError,
  /** When true, primary confirm is disabled (e.g. while a secondary in-modal action runs). */
  confirmDisabled = false,
  /** Max width of the dialog panel in px (e.g. wider rich content). */
  contentMaxWidth,
  /** When false, clicking the dimmed backdrop does not dismiss (must use a button). */
  closeOnBackdrop = true,
  /** When false, Escape does not dismiss (aligned with ModalShell.closeOnEscape). */
  closeOnEscape = true,
}) {
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) {
      setError(null);
      setConfirming(false);
    }
  }, [open]);

  const handleClose = async (confirmed) => {
    if (confirmed) {
      if (!onConfirm) return;
      setError(null);
      setConfirming(true);
      try {
        const result = onConfirm();
        if (result != null && typeof result.then === "function") {
          await result;
        }
        setConfirming(false);
        // Parent typically closes in its async onConfirm (e.g. setConfirmDialog({ open: false }))
      } catch (err) {
        const msg = err && typeof err.message === "string" ? err.message : "Something went wrong.";
        setError(msg);
        setConfirming(false);
        if (onError) onError(err);
      }
    } else if (onCancel) {
      onCancel();
    }
  };

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (onCancel) {
          onCancel();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, closeOnEscape, onCancel]);

  if (!open) return null;

  const isConfirm = mode === "confirm";
  const finalConfirmLabel = confirmLabel || (isConfirm ? "Confirm" : "OK");
  const finalCancelLabel = cancelLabel || "Cancel";
  const isStringMessage = typeof message === "string";
  const panelMaxWidth =
    contentMaxWidth != null && Number.isFinite(Number(contentMaxWidth))
      ? Math.max(320, Number(contentMaxWidth))
      : 420;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: "12px",
        boxSizing: "border-box",
      }}
      onClick={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) {
          handleClose(false);
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "confirmation-modal-title" : undefined}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          maxWidth: `${panelMaxWidth}px`,
          width: "100%",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
          padding: "16px 18px 14px",
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <h3
            id="confirmation-modal-title"
            style={{
              margin: "0 0 6px 0",
              fontSize: "1rem",
              fontWeight: 600,
              color: "#212529",
            }}
          >
            {title}
          </h3>
        )}
        <div
          style={{
            fontSize: "13px",
            color: "#212529",
            margin: "4px 0 10px 0",
            ...(isStringMessage ? { whiteSpace: "pre-wrap" } : {}),
          }}
        >
          {message}
        </div>
        {error && (
          <div
            style={{
              fontSize: "13px",
              color: "#b02a37",
              margin: "8px 0 6px 0",
              padding: "8px 10px",
              background: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
            }}
          >
            {error}
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: "6px",
          }}
        >
          {isConfirm && (
            <button
              type="button"
              onClick={() => handleClose(false)}
              disabled={confirming}
              style={{
                padding: "6px 12px",
                fontSize: "12px",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
                background: "#fff",
                color: "#495057",
                cursor: confirming ? "not-allowed" : "pointer",
              }}
            >
              {finalCancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={() => handleClose(true)}
            disabled={confirming || confirmDisabled}
            style={{
              padding: "6px 14px",
              fontSize: "12px",
              borderRadius: "4px",
              border: "none",
              background: "#0d6efd",
              color: "#fff",
              cursor: confirming || confirmDisabled ? "not-allowed" : "pointer",
              fontWeight: 500,
            }}
          >
            {confirming ? "…" : finalConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmationModal;

